<template>
  <!-- Carte partageable, format portrait 9/16 comme le PNG exporté
       (recap-image-layout.ts : 720x1280). Elle porte la couleur pastel de
       l'enfant : c'est SON bilan, pas une ligne de tableau de bord. -->
  <div
    :class="childColorClass(child?.color)"
    class="recap-card relative overflow-hidden rounded-3xl border border-line p-5 flex flex-col aspect-[9/16]"
  >
    <!-- Pas de bandeau "Bilan de la semaine" ici, contrairement au PNG exporté :
         dans l'app la carte s'ouvre sous un en-tête qui porte déjà ce titre, et
         la ligne du dashboard aussi. Trois fois le même intitulé à l'écran. -->

    <!-- Bandeau DÉMO. Les fixtures reprennent les VRAIS prénoms et avatars du
         foyer, donc seuls les chiffres sont inventés : sans marquage, la carte
         se lit comme un vrai bilan et on part debugger un calcul qui n'a jamais
         tourné. Posé au-dessus de la scène (z-10) car celle-ci déborde jusqu'au
         bord de la carte. Chaîne en dur, non traduite, comme le toast DEV de
         WeeklyRecapEntry : ce bandeau n'existe qu'en développement. -->
    <div
      v-if="demo"
      data-testid="recap-demo-badge"
      class="absolute top-3 right-3 z-10 rounded-full bg-ink/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.09em] text-warm"
    >
      Données de démo
    </div>

    <!-- La trame de l'enfant (voir `.child-pattern` dans index.css). -->
    <span aria-hidden="true" :class="childColorClass(child?.color)" class="recap-card__pattern child-pattern" />

    <!-- La scène : le décor que l'enfant s'est choisi dans la boutique, avec son
         personnage dessus. C'est ce qui rend deux cartes vraiment différentes,
         bien plus qu'une teinte : un renard sur un stade et un hérisson au bord
         de l'eau ne se confondent pas. Même composition que HomeKidTile, en
         grand. Le décor se fond dans la carte par un masque (jamais un dégradé
         vers `transparent` : WebKit y passe par du gris). -->
    <div v-if="hasScene" class="recap-card__scene relative -mx-5 -mt-5 shrink-0">
      <div
        v-if="backgroundImage"
        class="recap-card__scene-bg absolute inset-0 bg-cover bg-center"
        :style="{ backgroundImage: `url(${backgroundImage})` }"
      />
      <ChildAvatar
        :child="sceneChild as never"
        size="2xl"
        class-name="recap-card__scene-char"
        :show-points="false"
        :clickable="false"
        :hide-background="true"
        :rounded="false"
      />
    </div>

    <!-- Coeur de carte : au format story 9/16 le contenu ne remplit jamais la
         hauteur. Il est centré dans ce qui reste sous la scène plutôt
         qu'empilé en haut, sinon la moitié basse se lit comme un trou. -->
    <div class="relative flex-1 min-h-0 flex flex-col justify-center gap-4 py-3">

    <!-- Enfant. Sans scène (enfant sorti du foyer, ou avatar jamais configuré),
         on retombe sur la pastille : la carte doit rester lisible seule. -->
    <div class="relative flex items-center gap-3">
      <span
        v-if="!hasScene"
        aria-hidden="true"
        class="recap-card__avatar w-16 h-16 rounded-full overflow-hidden shrink-0 flex items-center justify-center"
      >
        <img
          v-if="snapshotAnimalImage"
          :src="snapshotAnimalImage"
          alt=""
          class="w-full h-full object-contain p-1"
        />
        <span v-else class="text-[22px] font-bold text-[var(--child-900)]">{{ childInitial }}</span>
      </span>
      <span class="ob-display text-[20px] leading-tight text-[var(--child-900)] truncate">
        {{ recap.child_name_snapshot }}
      </span>
    </div>

    <!-- Fait marquant -->
    <p v-if="recap.headline_key" class="relative ob-display text-[24px] leading-[1.12] text-ink text-balance">
      {{ t(`weeklyRecap.headline.${recap.headline_key}`, recap.headline_params ?? {}) }}
    </p>

    <!-- Tâches de la semaine : la carte portait ces chiffres sans jamais les
         montrer, et se retrouvait aux trois quarts vide. -->
    <div v-if="recap.tasks_total > 0" class="relative">
      <div class="flex items-baseline gap-1.5">
        <span class="text-[30px] font-bold leading-none text-[var(--child-900)]">{{ recap.tasks_completed }}</span>
        <span class="text-[13px] text-ink-3">
          {{ t('weeklyRecap.card.outOf', { total: recap.tasks_total }) }}
        </span>
      </div>
      <div class="mt-2 h-2 rounded-full bg-white/70 overflow-hidden">
        <div class="h-full rounded-full bg-[var(--child-500)]" :style="{ width: `${completionPercent}%` }" />
      </div>
    </div>

    <!-- Récompenses déclenchées. L'intitulé n'est pas décoratif : sans lui,
         "Glace" et "Sortie vélo" sont des mots posés là pour qui reçoit la
         carte sans connaître l'app. -->
    <div v-if="visibleRewards.length" class="relative">
      <p class="text-[10px] uppercase tracking-[0.1em] font-semibold text-[var(--child-900)] opacity-65 mb-1.5">
        {{ t('weeklyRecap.card.rewardsLabel') }}
      </p>
      <div class="flex flex-wrap gap-1.5">
        <span
          v-for="reward in visibleRewards"
          :key="reward.id"
          data-testid="recap-reward"
          class="flex items-center gap-1.5 bg-white/80 text-ink-2 rounded-full px-2.5 py-1 text-[12px] font-medium"
        >
          <LucideIcon v-if="reward.icon" :name="reward.icon" :size="13" />
          <span class="truncate max-w-[11ch]">{{ reward.title }}</span>
        </span>
        <span v-if="extraRewardsCount > 0" data-testid="recap-rewards-more" class="text-[12px] text-ink-3 self-center">
          {{ t('weeklyRecap.rewardsMore', { count: extraRewardsCount }, extraRewardsCount) }}
        </span>
      </div>
    </div>

    <!-- Bloc variable (série, prochain palier, collection) -->
    <div
      v-if="recap.variable_block_type"
      class="relative flex items-center gap-2 rounded-2xl bg-white/70 px-3 py-2.5"
    >
      <LucideIcon :name="variableBlockIcon" :size="15" class="shrink-0 text-[var(--child-900)]" />
      <span class="text-[13px] text-[var(--child-900)] leading-snug">
        {{ t(`weeklyRecap.variableBlock.${recap.variable_block_type}`, variableBlockParams) }}
      </span>
    </div>

      <!-- Semaine couverte : la carte partagée sort du contexte de l'app, elle
           doit dire d'elle-même de quelle semaine elle parle. -->
      <p class="relative text-[12px] font-medium text-[var(--child-900)] opacity-70">
        {{ weekLabel }}
      </p>
    </div>

    <!-- Pied : la signature seule. Le bouton de partage a été retiré, le geste
         n'a pas trouvé son public. Toute la mécanique reste en place derrière
         (useWeeklyRecapShare et l'edge function generate-recap-image) : c'est
         la surface qui disparaît, pas la fonctionnalité. -->
    <div class="relative flex items-center">
      <span class="text-[11px] text-ink-4">withharmonia.com</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { WeeklyRecap, WeeklyRecapVariableBlockType } from '@/models/WeeklyRecap'
import { childColorClass } from '@/lib/colors'
import { useAvatarsStore } from '@/stores/avatars'
import { getAnimal } from '@/services/animal-avatar.service'
import LucideIcon from '@/components/common/LucideIcon.vue'
import ChildAvatar from '@/components/common/ChildAvatar.vue'

// Plafond d'affichage des récompenses sur la carte (spec §5 : "plafonnées à
// 2 ou 3. Au delà, afficher les plus significatives puis 'et N autres'").
const REWARDS_DISPLAY_CAP = 3

const VARIABLE_BLOCK_ICONS: Record<WeeklyRecapVariableBlockType, string> = {
  streak: 'flame',
  next_milestone: 'target',
  collection_unlock: 'sparkles',
}

const props = defineProps<{
  recap: WeeklyRecap
  /** Enfant du foyer, s'il est encore présent : donne la couleur et l'avatar animé. */
  child?: { id: string; name?: string | null; color?: string | null; avatar?: string | null }
  /** Chiffres issus des fixtures DEV et non de la base : la carte le dit à l'écran. */
  demo?: boolean
}>()

const { t, locale } = useI18n()
const avatarsStore = useAvatarsStore()

// L'id vient de la LIGNE de bilan, pas de la prop `child` : celle-ci descend
// d'un `ref([])` que le dashboard remplit après un appel réseau, et faire
// dépendre la scène de son arrivée revenait à peindre la carte en repli dégradé
// dans l'intervalle. La ligne, elle, porte toujours son child_id.
const sceneChildId = computed(() => props.child?.id ?? props.recap.child_id)

// Les configs d'avatar sont préchargées pour tout le foyer au boot
// (householdHydration → preloadChildAvatars) : lecture de cache synchrone, pas
// de requête par enfant. Même source que HomeKidTile.
const backgroundImage = computed(() => avatarsStore.getBackgroundForChild(sceneChildId.value)?.imagePath)

/**
 * La scène n'est rendue que s'il y a de quoi la peupler. Sans elle (enfant
 * sorti du foyer, avatar jamais configuré), la carte retombe sur la pastille et
 * la trame : elle doit rester tenue toute seule.
 */
const hasScene = computed(() =>
  !!backgroundImage.value || !!avatarsStore.getAnimalForChild(sceneChildId.value),
)

// ChildAvatar attend un enfant du foyer ; la ligne de bilan en dit assez pour le
// décrire quand la liste n'est pas encore là.
const sceneChild = computed(() => props.child ?? {
  id: props.recap.child_id,
  name: props.recap.child_name_snapshot,
  color: null,
  avatar: null,
})

/**
 * Repli quand il n'y a pas de scène. `child_avatar_snapshot` porte un
 * IDENTIFIANT d'animal ("dog"), pas une URL : sur 59 lignes en base, aucune n'en
 * contient une. Servi tel quel en `src`, il ne pouvait donner qu'une image
 * cassée. On le résout donc comme un animal, et on tolère une URL au cas où le
 * format changerait.
 */
const snapshotAnimalImage = computed(() => {
  const snapshot = props.recap.child_avatar_snapshot
  if (!snapshot) return null
  if (/^(https?:|data:|\/)/i.test(snapshot)) return snapshot
  return getAnimal(snapshot)?.baseImage ?? null
})

// child_name_snapshot est figé à la génération et peut manquer sur une ligne
// ancienne : l'initiale ne doit pas faire tomber le rendu de toute la carte.
const childInitial = computed(() => (props.recap.child_name_snapshot ?? '').charAt(0).toUpperCase())

// Dates DB au format YYYY-MM-DD : découpées à la main plutôt que passées à
// `new Date(string)`, qui les lit en UTC et peut reculer d'un jour.
const weekLabel = computed(() => {
  const format = new Intl.DateTimeFormat(locale.value, { day: 'numeric', month: 'long' })
  const toDate = (value: string) => {
    const [y, m, d] = value.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  if (!props.recap.week_start || !props.recap.week_end) return ''
  return t('weeklyRecap.dashboard.weekRange', {
    start: format.format(toDate(props.recap.week_start)),
    end: format.format(toDate(props.recap.week_end)),
  })
})

const visibleRewards = computed(() => (props.recap.rewards_snapshot ?? []).slice(0, REWARDS_DISPLAY_CAP))
const extraRewardsCount = computed(() => Math.max(0, (props.recap.rewards_snapshot ?? []).length - REWARDS_DISPLAY_CAP))

const completionPercent = computed(() => {
  const { tasks_completed: done, tasks_total: total } = props.recap
  if (!total) return 0
  return Math.min(100, Math.round((done / total) * 100))
})

const variableBlockIcon = computed(() =>
  props.recap.variable_block_type ? VARIABLE_BLOCK_ICONS[props.recap.variable_block_type] : 'sparkles',
)

// Le streak (design doc §12 : affiché pour tous, jamais gaté) n'est pas
// encapsulé dans variable_block_payload pour le cas 'streak' — il vient de
// streak_at_generation, figé au calcul comme le reste de la carte.
const variableBlockParams = computed(() => {
  if (props.recap.variable_block_type === 'streak') {
    return { count: props.recap.streak_at_generation ?? 0 }
  }
  return props.recap.variable_block_payload ?? {}
})
</script>

<style scoped>
/* La carte est un OBJET COLORÉ, pas une feuille blanche : au format story 9/16
   le contenu ne remplit jamais la hauteur, et sur du blanc le reste se lisait
   comme un trou. Le lavis va du pastel de l'enfant en haut à sa teinte la plus
   claire en bas, donc la respiration reste une surface tenue.
   Deux couleurs franches, jamais un fondu vers `transparent` : WebKit interpole
   `transparent` via du gris et laisse une bande sale au milieu. */
.recap-card {
  background: linear-gradient(to bottom, var(--child-100) 0%, var(--child-50) 100%);
}

/* Motif de fond : la trame de l'enfant, tracée par `.child-pattern` (index.css)
   dans sa teinte 200, sur la moitié haute où le contenu est le plus aéré. */
.recap-card__pattern {
  position: absolute;
  inset: 0;
  opacity: 0.5;
  pointer-events: none;
  /* Masque vertical : la trame porte le haut de la carte et s'efface avant le
     pied, pour ne jamais concurrencer le bouton de partage. */
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 35%, rgb(0 0 0 / 0) 78%);
  mask-image: linear-gradient(to bottom, #000 0%, #000 35%, rgb(0 0 0 / 0) 78%);
}

/* Disque d'avatar de repli (pas de scène) : la teinte 200 le détache de la
   trame, l'anneau blanc le détache du disque. */
.recap-card__avatar {
  background: var(--child-200);
  box-shadow: 0 0 0 3px #FFFFFF;
}

/* La scène. Sa hauteur est une PROPORTION de la carte (aspect ratio sur toute
   la largeur), pas un nombre de pixels : la carte est dimensionnée sur la
   hauteur disponible de la feuille, qui varie d'un appareil à l'autre.
   Elle est l'élément ÉLASTIQUE de la carte (`flex: 0 1 auto`) : une semaine
   très fournie (4 récompenses + palier + série) dépasse sinon la hauteur du
   9/16 et pousse le bouton de partage hors du cadre. Le décor étant en
   `cover` et le personnage en `contain`, la scène se recadre proprement. */
.recap-card__scene {
  aspect-ratio: 16 / 10;
  flex: 0 1 auto;
  min-height: 96px;
  overflow: hidden;
  background: var(--child-200);
}

/* Le décor s'efface vers le bas pour se fondre dans la carte. Un MASQUE et non
   un dégradé de couleur par-dessus : le masque n'interpole que l'alpha, donc
   pas de bande grise WebKit, et il n'a pas besoin de connaître la teinte
   exacte du fond de carte à cet endroit. */
.recap-card__scene-bg {
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 58%, rgb(0 0 0 / 0) 100%);
  mask-image: linear-gradient(to bottom, #000 0%, #000 58%, rgb(0 0 0 / 0) 100%);
}

/* Le personnage n'est PAS masqué : il doit rester net jusqu'aux pattes alors
   que son décor se dissout derrière lui. Il déborde légèrement vers le bas,
   comme sur les tuiles du dashboard, pour poser sur le contenu. */
.recap-card :deep(.recap-card__scene-char) {
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 118% !important;
  top: -6%;
}

.recap-card :deep(.recap-card__scene-char .avatar-image) {
  object-fit: contain;
}

</style>
